import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useT, type StringId } from '../lib/i18n'
import { OnboardingProvider, useOnboarding, type OnboardingStep } from '../onboarding/OnboardingContext'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { EmptyState } from '../ui/EmptyState'
import { Spinner } from '../ui/Spinner'
import { StepProgress } from '../ui/StepProgress'

/**
 * The dictionary keys for each step, written out.
 *
 * **This was a template and a cast, and the cast was load-bearing in the wrong
 * direction.** `` `onboarding.step.${slug}.why` as StringId `` builds the key
 * from a runtime slug, so `check:i18n` cannot see it — the scanner looks for
 * literals — and the cast tells the compiler to stop looking too. Deleting
 * `onboarding.step.goal.why` from the dictionary left `check:i18n` green at 120
 * strings and `tsc --noEmit` silent, and the step then threw at runtime, because
 * `translate()` is `STRINGS[key][locale]` and `undefined[locale]` is a
 * `TypeError`. The old comment claimed the opposite — that an unknown slug was
 * "caught here". It was not caught anywhere. Found at review of SUAP-0204.
 *
 * Spelled out and `satisfies`-checked, a missing dictionary entry is a compile
 * error on this table, which is a guard that exists rather than one that reads
 * well. The runtime half — a slug the server sends that has no row here — is
 * handled by the caller rather than cast away.
 */
const STEP_COPY = {
  body: { title: 'onboarding.step.body.title', why: 'onboarding.step.body.why' },
  activity: { title: 'onboarding.step.activity.title', why: 'onboarding.step.activity.why' },
  goal: { title: 'onboarding.step.goal.title', why: 'onboarding.step.goal.why' },
  schedule: { title: 'onboarding.step.schedule.title', why: 'onboarding.step.schedule.why' },
  health: { title: 'onboarding.step.health.title', why: 'onboarding.step.health.why' },
  plan: { title: 'onboarding.step.plan.title', why: 'onboarding.step.plan.why' },
} satisfies Record<string, { title: StringId; why: StringId }>

/** The copy for a slug, or null when the server sends one this build has none for. */
function stepCopy(slug: string): { title: StringId; why: StringId } | null {
  return Object.prototype.hasOwnProperty.call(STEP_COPY, slug)
    ? STEP_COPY[slug as keyof typeof STEP_COPY]
    : null
}

/**
 * The wizard shell.
 *
 * **This is the frame, not the six screens.** It owns the step order, which step
 * you land on, moving between them, and the save that happens as each step is
 * left. The step bodies are their own tickets (`SUAP-0205`–`0207`,
 * `SUAP-1210`), and the plan preview is `SUAP-0210` — see this ticket's
 * `## Amendment` for why that one left.
 *
 * **The current step is in the query string, not the path.** `?step=body` rather
 * than `/onboarding/body`, and the reason is the verification suite rather than
 * taste: `assertNoRouteDrift` requires every `path="…"` in `App.tsx` to appear in
 * `scripts/routes.mjs`, and `check:screens` requires every manifest entry to be
 * claimed by exactly one row in `SCREENS.md`. Six step paths would need six of
 * each, and `SCREENS.md` already models body+activity as one screen owned by
 * `SUAP-0205` — so a path per step would mean restructuring another ticket's
 * rows to satisfy a guard. A query string still pushes history, so browser Back
 * moves between steps, which is what actually matters here.
 *
 * SUAP-0204.
 */
export function Onboarding() {
  return (
    <OnboardingProvider>
      <Wizard />
    </OnboardingProvider>
  )
}

function Wizard() {
  const { t } = useT()
  const { status, state, error, save, skip } = useOnboarding()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const requested = params.get('step')

  /**
   * Which step to render, and it is not always the one that was asked for.
   *
   * A step the user cannot reach yet resolves to the first unresolved one. That
   * is **acceptance criterion 3**: the plan preview is last, so it is
   * unreachable until the health screen is complete, and typing `?step=plan`
   * into the address bar gets the same answer as clicking Next would. The rule
   * lives in the server's `reachable` flag rather than being recomputed here —
   * the API refuses an out-of-reach step too, so this redirect is the courtesy
   * and the 422 is the enforcement.
   */
  const active = useMemo<OnboardingStep | null>(() => {
    if (state === null) return null
    const asked = state.steps.find((step) => step.slug === requested)
    if (asked !== undefined && asked.reachable) return asked
    return state.steps.find((step) => step.slug === state.current_step) ?? null
  }, [state, requested])

  /**
   * Keep the URL naming the step that is actually on screen.
   *
   * Without this the shell renders the right step and the address bar says
   * something else — `/onboarding` with no parameter on arrival, and
   * `?step=plan` still showing after the redirect to `body`. Both were found by
   * `ui:drive:onboarding`, and both matter beyond the script: a URL that does
   * not name what is rendered cannot be shared, cannot be reloaded onto the same
   * step, and makes browser Back move to a step the user was never on.
   *
   * `replace`, so correcting the URL does not push a history entry — otherwise
   * Back returns to the unreachable step and bounces forward again.
   */
  useEffect(() => {
    if (state === null) return

    // A finished wizard names no step. Clearing it is what stops a return visit
    // from resuming a step that no longer exists to resume.
    if (state.completed_at !== null) {
      if (requested !== null) setParams({}, { replace: true })
      return
    }
    if (active !== null && requested !== active.slug) {
      setParams({ step: active.slug }, { replace: true })
    }
  }, [state, active, requested, setParams])

  const goTo = useCallback(
    (slug: string) => {
      // `replace: false`, so Back is a step back rather than a jump out of the
      // wizard.
      setParams({ step: slug })
    },
    [setParams],
  )

  const advance = useCallback(
    (next: string) => {
      if (next === '') {
        // Every step resolved. The wizard is finished and the URL should stop
        // naming a step, or Back lands on a step that now redirects.
        navigate('/onboarding', { replace: true })
        return
      }
      goTo(next)
    },
    [goTo, navigate],
  )

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">{t('state.loading')}</span>
      </div>
    )
  }

  if (status === 'error' || state === null) {
    return (
      <EmptyState emoji="⚠️" title={t('error.title')} body={t('error.body')} />
    )
  }

  // A finished wizard renders its own end rather than a step. Reached by
  // completing the last step, and by returning to /onboarding afterwards.
  if (active === null || state.completed_at !== null) {
    return (
      <div className="flex flex-col gap-4">
        <EmptyState emoji="🥄" title={t('onboarding.done.title')} body={t('onboarding.done.body')} />
        <Button variant="ink" block onClick={() => navigate('/today')}>
          {t('action.home')}
        </Button>
      </div>
    )
  }

  const index = state.steps.findIndex((step) => step.slug === active.slug)
  const previous = index > 0 ? state.steps[index - 1] : undefined
  const isLast = index === state.steps.length - 1
  const copy = stepCopy(active.slug)

  // The server decides what "next" is, because it decides what is resolved. The
  // shell asking would be a second copy of the rule.
  const onNext = async () => {
    try {
      await save(active.slug)
      const next = state.steps.find((step) => step.slug !== active.slug && !step.complete)
      advance(next?.slug ?? '')
    } catch {
      // Stored on the context and rendered below; the wizard stays put, which is
      // the point — advancing past a step that did not save is the state AC-1
      // says cannot happen.
    }
  }

  const onSkip = async () => {
    try {
      await skip(active.slug)
      const next = state.steps.find((step) => step.slug !== active.slug && !step.complete)
      advance(next?.slug ?? '')
    } catch {
      /* see onNext */
    }
  }

  return (
    <div className="flex flex-col gap-4">
        <StepProgress current={index + 1} total={state.steps.length} />

        <Card>
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold text-ink">
              {copy === null ? t('onboarding.title') : t(copy.title)}
            </h2>
            {/* Every step says in one line why it is being asked. Acceptance
                criterion 6, and the reason the first five get answered at all.
                `data-step-why` is what ui:drive:onboarding reads to assert the
                line is really there — the criterion had no proof that could go
                red until it did. */}
            <p className="text-sm text-ink-soft" data-step-why={active.slug}>
              {copy === null ? t('onboarding.step.pending') : t(copy.why)}
            </p>
          </div>

          {/* The step body's slot. Its ticket fills it; until then the panel says
              so rather than rendering blank, which reads as a bug. */}
          <p className="mt-4 text-sm text-ink-soft" data-step-body={active.slug}>
            {t('onboarding.step.pending')}
          </p>
        </Card>

        {error !== null && (
          <p role="alert" className="text-sm text-over">
            {t('onboarding.error.save')}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <Button variant="ink" block onClick={() => void onNext()}>
            {isLast ? t('onboarding.action.finish') : t('onboarding.action.next')}
          </Button>

          {active.skippable && (
            <Button variant="ghost" block onClick={() => void onSkip()}>
              {t('onboarding.action.skip')}
            </Button>
          )}

          {previous !== undefined && (
            <Button variant="ghost" block onClick={() => goTo(previous.slug)}>
              {t('onboarding.action.back')}
            </Button>
          )}
        </div>
    </div>
  )
}
